import React, { useEffect, useMemo, useState } from "react";
import TrustdService from "../service/TrustdService";
import { convertToMMYYDDDD } from "../utils/DateUtils";
import AuthService from "../service/AmplifyAuth";
import { Button, Card, DetailBox, Panel, RatingStars } from "../components/UI";
import { useToast } from "../hooks";
import { useAppContext } from "../context/AppContext";
import { useNavigate, useRouteMatch } from "../router";

const STATE_GUESTS = "STATE_GUESTS";
const STATE_REVIEWS = "STATE_REVIEWS";
const ENABLE_GUEST_REVIEWS = false;

const FILTER_OPTIONS = [
  ["all", "All"],
  ["completed", "Completed"],
  ["current", "Currently Staying"],
  ["future", "Upcoming Stay"],
  ...(ENABLE_GUEST_REVIEWS ? [["waitingReview", "Pending Reviews"]] : []),
];

const FILTER_VALUES = new Set(FILTER_OPTIONS.map(([value]) => value));

function normalizeReviewsFilter(value) {
  const normalized = String(value || "");
  return FILTER_VALUES.has(normalized) ? normalized : "all";
}

function normalizeHostRating(review) {
  const rawRating =
    review?.host_rating ??
    review?.review_host_rating ??
    review?.Review?.host_rating ??
    review?.Review?.review_host_rating ??
    null;

  if (rawRating === null || rawRating === undefined || rawRating === "")
    return null;

  if (typeof rawRating === "number") {
    if (!Number.isFinite(rawRating)) return null;
    return Math.max(0, Math.min(5, Math.round(rawRating)));
  }

  const match = String(rawRating).match(/-?\d+(\.\d+)?/);
  if (!match) return null;

  const parsed = Number(match[0]);
  if (!Number.isFinite(parsed)) return null;
  return Math.max(0, Math.min(5, Math.round(parsed)));
}

function normalizeHostComments(review) {
  const rawComments =
    review?.host_comments ??
    review?.review_host_comments ??
    review?.Review?.host_comments ??
    review?.Review?.review_host_comments ??
    null;

  if (Array.isArray(rawComments))
    return rawComments.map((value) => String(value)).join(", ");
  if (rawComments === null || rawComments === undefined) return "";
  return String(rawComments);
}

function currentDate() {
  const current = new Date();
  return `${String(current.getMonth() + 1).padStart(2, "0")}/${String(current.getDate()).padStart(2, "0")}/${current.getFullYear()}`;
}

function startOfLocalDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseStayDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return startOfLocalDay(parsed);
}

function getGuestInitials(review) {
  return (
    [review?.first_name, review?.last_name]
      .filter(Boolean)
      .map((part) => String(part)[0]?.toUpperCase() || "")
      .join("") || "G"
  );
}

function getStayStatus(review) {
  const today = startOfLocalDay(new Date());
  const checkIn = parseStayDate(review?.check_in_date);
  const checkOut = parseStayDate(review?.check_out_date);

  if (checkOut && checkOut < today)
    return { label: "Completed", className: "completed" };
  if (checkIn && checkOut && checkIn <= today && checkOut >= today) {
    return { label: "Staying", className: "staying" };
  }
  return { label: "Upcoming", className: "upcoming" };
}

function canReviewGuest(review) {
  const checkOut = parseStayDate(review?.check_out_date);
  if (!checkOut) return false;
  return checkOut < startOfLocalDay(new Date());
}

export default function PastReviews() {
  const { commit } = useAppContext();
  const route = useRouteMatch();
  const navigate = useNavigate();
  const requestedFilter = normalizeReviewsFilter(route.query.filter);
  const [reviews, setReviews] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [pastReviewsCurrentFilter, setPastReviewsCurrentFilter] =
    useState(requestedFilter);
  const [currentStage, setCurrentStage] = useState(STATE_GUESTS);
  const [currentUser, setCurrentUser] = useState(null);
  const [openingQuickScoreFor, setOpeningQuickScoreFor] = useState("");
  const [form, setForm] = useState({
    guestStayReview1: false,
    guestStayReview2: false,
    guestStayReview3: false,
    guestStayReview4: false,
    hostRating: "",
    hostConsent: false,
    hostCommentsOther: "",
  });
  const addToast = useToast();

  const filterMapping = useMemo(
    () => ({
      all: [],
      current: [
        {
          field: "Payload.check_out_date",
          value: currentDate(),
          operator: "gte",
          isDate: true,
        },
        {
          field: "Payload.check_in_date",
          value: currentDate(),
          operator: "lte",
          isDate: true,
        },
      ],
      completed: [
        {
          field: "Payload.check_out_date",
          value: currentDate(),
          operator: "lt",
          isDate: true,
        },
      ],
      future: [
        {
          field: "Payload.check_in_date",
          value: currentDate(),
          operator: "gt",
          isDate: true,
        },
      ],
      ...(ENABLE_GUEST_REVIEWS
        ? {
            waitingReview: [
              { field: "Review.review_date", value: null, operator: "eq" },
              {
                field: "Payload.check_out_date",
                value: currentDate(),
                operator: "lt",
                isDate: true,
              },
            ],
          }
        : {}),
    }),
    [],
  );

  useEffect(() => {
    setPastReviewsCurrentFilter((currentFilter) =>
      currentFilter === requestedFilter ? currentFilter : requestedFilter,
    );
    setCurrentStage(STATE_GUESTS);
    commit("updatePastReviewsFilter", requestedFilter);
  }, [commit, requestedFilter]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pastReviewsCurrentFilter]);

  const refresh = () => {
    commit("updatePastReviewsFilter", pastReviewsCurrentFilter);
    setLoading(true);
    TrustdService.listReviewsByHostEmailFiltered(
      1,
      99999,
      filterMapping[pastReviewsCurrentFilter],
    )
      .then((data) => {
        const rows = (data.Data || []).map((review) => {
          const next = { ...review };
          next.host_rating = normalizeHostRating(review);
          next.host_comments = normalizeHostComments(review);
          if (next.listing_name && next.listing_name.length > 18)
            next.listing_name = `${next.listing_name.slice(0, 15)}...`;
          if (next.score === "AVERAGE TRUST") next.score = "GOOD";
          return next;
        });
        setReviews(rows);
        const reviewId = new URLSearchParams(window.location.search).get(
          "review_id",
        );
        if (ENABLE_GUEST_REVIEWS && reviewId) {
          const userReview = rows.find(
            (review) => review.trustd_reference === reviewId,
          );
          if (
            userReview &&
            userReview.host_rating === null &&
            canReviewGuest(userReview)
          )
            directReview(userReview);
        }
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const directReview = (review) => {
    if (!ENABLE_GUEST_REVIEWS) return;
    if (!canReviewGuest(review)) return;
    setCurrentUser(review);
    setCurrentStage(STATE_REVIEWS);
  };

  const submit = async () => {
    if (!ENABLE_GUEST_REVIEWS) return;
    if (!form.hostRating) {
      addToast({
        severity: "error",
        summary: "Error",
        detail: "Please rate before submitting.",
        life: 10000,
      });
      return;
    }
    if (!form.hostConsent) {
      addToast({
        severity: "error",
        summary: "Error",
        detail: "Please check the consent before submitting.",
        life: 10000,
      });
      return;
    }

    const comments = [];
    if (form.guestStayReview1) comments.push("Damaged/thrashed the property");
    if (form.guestStayReview2) comments.push("Noise complaints");
    if (form.guestStayReview3) comments.push("Insurance claim is filed");
    if (form.guestStayReview4) comments.push("Review blackmail threats");
    if (form.hostCommentsOther) comments.push(form.hostCommentsOther);

    const attrs = await AuthService.currentAuthenticatedUserAttributes();
    const payload = {
      match_request_id: currentUser.trustd_reference,
      first_name: currentUser.first_name,
      middle_name: currentUser.middle_name,
      last_name: currentUser.last_name,
      dob: currentUser.dob,
      host_email: attrs.username,
      guest_email: currentUser.guest_email,
      check_in_date: currentUser.check_in_date,
      check_out_date: currentUser.check_out_date,
      score: currentUser.score,
      review_host_rating: Number(form.hostRating),
      review_host_comments: comments,
      review_emails: [],
      _class: "com.td.tes.model.MatchReview",
    };

    await TrustdService.submitHostReview(payload);
    addToast({
      severity: "success",
      summary: "Success",
      detail: "Review is successfully sent. Redirecting...",
      life: 10000,
    });
    window.setTimeout(() => {
      window.location.href = "/reviews";
    }, 3000);
  };

  const reset = () =>
    setForm({
      guestStayReview1: false,
      guestStayReview2: false,
      guestStayReview3: false,
      guestStayReview4: false,
      hostRating: "",
      hostConsent: false,
      hostCommentsOther: "",
    });
  const goBack = () => {
    reset();
    setCurrentUser(null);
    setCurrentStage(STATE_GUESTS);
  };

  const setFilter = (value) => {
    const nextFilter = normalizeReviewsFilter(value);
    const params = new URLSearchParams(window.location.search);
    params.set("filter", nextFilter);
    params.delete("review_id");
    setPastReviewsCurrentFilter(nextFilter);
    setCurrentStage(STATE_GUESTS);
    commit("updatePastReviewsFilter", nextFilter);
    navigate(`/reviews?${params.toString()}`);
  };

  const filteredReviews = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return reviews;
    return reviews.filter((review) => {
      const haystack = [
        review.first_name,
        review.middle_name,
        review.last_name,
        review.listing_name,
        review.listing_channel,
        review.guest_email,
        review.trustd_reference,
        review.guest_form_id,
        review.booking_id,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [reviews, searchTerm]);

  const openQuickScore = async (trustdReference) => {
    const reference = String(trustdReference || "").trim();
    if (!reference || openingQuickScoreFor) return;
    try {
      setOpeningQuickScoreFor(reference);
      const url = await TrustdService.createQuickScoreShareLink(reference);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      addToast({
        severity: "error",
        summary: "Link Error",
        detail: String(
          error?.message || "Quick score link could not be prepared.",
        ),
        life: 7000,
      });
    } finally {
      setOpeningQuickScoreFor("");
    }
  };

  return (
    <div className="guests-page guests-modern">
      {!ENABLE_GUEST_REVIEWS || currentStage === STATE_GUESTS ? (
        <Card className="cardCenter cardW100 guests-list-card">
          <div className="guests-list-head">
            <div>
              <p className="dashboard-section-kicker">Guest Overview</p>
              <h2>My Guests</h2>
            </div>
          </div>

          <div className="guests-modern-toolbar">
            <div
              className="guests-filter-row"
              role="tablist"
              aria-label="Guest filters"
            >
              {FILTER_OPTIONS.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={pastReviewsCurrentFilter === value}
                  className={`guests-filter-chip ${pastReviewsCurrentFilter === value ? "active" : ""}`}
                  onClick={() => setFilter(value)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="guests-modern-search" role="search">
              <i className="pi pi-search" aria-hidden="true" />
              <input
                type="search"
                value={searchTerm}
                placeholder="Search guests or listings..."
                aria-label="Search guests or listings"
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
          </div>

          <div className="guests-modern-summary">
            <p>
              <strong>{filteredReviews.length}</strong> guests
            </p>
            <p>Your guest pipeline, all in one place.</p>
          </div>

          <div className="table-scroll guests-table-wrap">
            {loading ? (
              <div
                className="guests-table-loading-overlay"
                aria-live="polite"
                aria-label="Loading guest table"
              >
                <div className="loader guests-table-loader" />
              </div>
            ) : null}
            <table className="data-table guests-list-table">
              <thead>
                <tr>
                  <th>First Name</th>
                  <th>Middle Name</th>
                  <th>Last Name</th>
                  <th>Date of Birth</th>
                  <th>Check-in</th>
                  <th>Check-out</th>
                  <th>Listing</th>
                  <th>Channel</th>
                  <th>Status</th>
                  {ENABLE_GUEST_REVIEWS ? <th>Rating</th> : null}
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {!loading && filteredReviews.length === 0 ? (
                  <tr>
                    <td
                      colSpan={ENABLE_GUEST_REVIEWS ? 11 : 10}
                      className="guests-empty-row"
                    >
                      No guests found for this filter.
                    </td>
                  </tr>
                ) : (
                  filteredReviews.map((review) => {
                    const displayStatus = getStayStatus(review);
                    const canSubmitReview =
                      ENABLE_GUEST_REVIEWS &&
                      review.host_rating === null &&
                      canReviewGuest(review);
                    const hasReport = Boolean(review.trustd_reference);
                    return (
                      <tr
                        key={
                          review.trustd_reference ||
                          review.guest_form_id ||
                          `${review.guest_email}-${review.check_in_date}`
                        }
                      >
                        <td>
                          <div className="guests-name-cell">
                            <span>{getGuestInitials(review)}</span>
                            <strong>{review.first_name || "-"}</strong>
                          </div>
                        </td>
                        <td>{review.middle_name || "-"}</td>
                        <td>{review.last_name || "-"}</td>
                        <td>{review.dob || "-"}</td>
                        <td>{review.check_in_date || "-"}</td>
                        <td>{review.check_out_date || "-"}</td>
                        <td title={review.listing_name}>
                          {review.listing_name || "-"}
                        </td>
                        <td>
                          <span className="guests-channel-tag">
                            {review.listing_channel || "-"}
                          </span>
                        </td>
                        <td>
                          {canSubmitReview ? (
                            <button
                              type="button"
                              onClick={() => directReview(review)}
                              className="guests-review-cta"
                            >
                              Pending Review
                            </button>
                          ) : (
                            <span
                              className={`guests-status-pill ${displayStatus.className}`}
                            >
                              {displayStatus.label}
                            </span>
                          )}
                        </td>
                        {ENABLE_GUEST_REVIEWS ? (
                          <td>
                            <RatingStars value={review.host_rating} />
                          </td>
                        ) : null}
                        <td>
                          <button
                            type="button"
                            className="guests-view-link"
                            disabled={
                              !hasReport ||
                              openingQuickScoreFor === review.trustd_reference
                            }
                            onClick={() =>
                              openQuickScore(review.trustd_reference)
                            }
                            title={
                              hasReport
                                ? undefined
                                : "Report will be available after the guest submits the form."
                            }
                          >
                            {!hasReport
                              ? "Pending..."
                              : openingQuickScoreFor === review.trustd_reference
                                ? "Preparing..."
                                : "View Report"}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      ) : ENABLE_GUEST_REVIEWS ? (
        <Card className="cardCenter cardW100 guests-review-card">
          <div className="guests-list-head">
            <div>
              <p className="dashboard-section-kicker">Host Feedback</p>
              <h2>Review {currentUser.first_name}</h2>
            </div>
            <p className="dashboard-section-note">
              {currentUser.first_name} {currentUser.last_name} has checked out.
              Please provide your honest and objective review here to help other
              hosts.
            </p>
          </div>

          <Panel header="Guest Information" className="guests-review-panel">
            <div className="guests-account-grid">
              <DetailBox label="Email" value={currentUser.guest_email} />
              <DetailBox
                label="Name"
                value={`${currentUser.first_name} ${currentUser.last_name}`}
              />
              <DetailBox
                label="Listing"
                value={currentUser.listing_name}
              />
              <DetailBox
                label="Check-in Date"
                value={convertToMMYYDDDD(currentUser.check_in_date)}
              />
              <DetailBox
                label="Check-out Date"
                value={convertToMMYYDDDD(currentUser.check_out_date)}
              />
              <DetailBox
                label="Trustd Reference"
                value={currentUser.trustd_reference}
              />
            </div>
          </Panel>

          <Panel header="Your Review" className="guests-review-panel">
            <div className="guests-review-form">
              <p className="guests-review-title">
                How was the overall guest stay? (select all that apply)
              </p>
              {[
                ["guestStayReview1", "Damaged/thrashed the property"],
                ["guestStayReview2", "Noise complaints"],
                ["guestStayReview3", "Insurance claim is filed"],
                ["guestStayReview4", "Review blackmail threats"],
              ].map(([field, label]) => (
                <label key={field} className="guests-check-option">
                  <input
                    type="checkbox"
                    checked={form[field]}
                    onChange={(event) =>
                      setForm((f) => ({ ...f, [field]: event.target.checked }))
                    }
                  />
                  <span>{label}</span>
                </label>
              ))}

              <label className="guests-other-row">
                <span>Other</span>
                <input
                  className="form-control"
                  value={form.hostCommentsOther}
                  maxLength="50"
                  onChange={(event) =>
                    setForm((f) => ({
                      ...f,
                      hostCommentsOther: event.target.value,
                    }))
                  }
                />
              </label>

              <p className="guests-review-title">
                Do you recommend {currentUser.first_name} to other hosts?
              </p>
              <div className="guests-rating-grid">
                {[5, 4, 3, 2, 1].map((value) => (
                  <label key={value} className="guests-rating-option">
                    <input
                      type="radio"
                      name="hostRating"
                      checked={form.hostRating === String(value)}
                      onChange={() =>
                        setForm((f) => ({ ...f, hostRating: String(value) }))
                      }
                    />
                    <span className="stars">{"★".repeat(value)}</span>
                    <span className="empty-stars">{"★".repeat(5 - value)}</span>
                  </label>
                ))}
              </div>

              <label className="guests-consent-label">
                <input
                  type="checkbox"
                  checked={form.hostConsent}
                  onChange={(event) =>
                    setForm((f) => ({
                      ...f,
                      hostConsent: event.target.checked,
                    }))
                  }
                />
                <span>
                  I hereby agree and accept that reviews above are genuine,
                  honest, unbiased, and objective.
                </span>
              </label>

              <div className="guests-actions mt-2">
                <Button label="Send Review" onClick={submit} />
                <Button
                  label="Reset"
                  onClick={reset}
                  className="guests-action-secondary"
                />
                <Button
                  label="Back to My Guests"
                  onClick={goBack}
                  className="guests-action-secondary"
                />
              </div>
            </div>
          </Panel>
        </Card>
      ) : null}
    </div>
  );
}
